import { api } from '../utils/api';

// Finance Transaction types
export interface FinanceTransaction {
  id: string;
  type: 'deposit' | 'withdrawal';
  amount: number;
  status: 'pending' | 'completed' | 'failed' | 'cancelled';
  mpesaReference?: string;
  timestamp: Date;
  description: string;
  fee?: number;
  netAmount?: number;
  phoneNumber: string;
  userType: 'competitor' | 'student' | 'teacher';
  metadata?: {
    checkoutRequestId: string;
    merchantRequestId: string;
    resultCode?: string;
    resultDesc?: string;
    completedAt?: Date;
    failedAt?: Date;
  };
}

export interface MPESAPaymentRequest {
  amount: number;
  phoneNumber: string;
  userType: 'competitor' | 'student' | 'teacher';
  description?: string;
}

export interface MPESAPaymentResponse {
  success: boolean;
  message: string;
  data: {
    merchantRequestID: string;
    checkoutRequestID: string;
    responseCode: string;
    responseDescription: string;
    customerMessage: string;
    amount: number;
    phoneNumber: string;
    userType: string;
    userId: string;
  };
}

export interface PaymentStatus {
  success: boolean;
  message: string;
  data: {
    merchantRequestID: string;
    checkoutRequestID: string;
    resultCode: string;
    resultDesc: string;
    mpesaReceiptNumber?: string;
    status: 'pending' | 'completed' | 'failed';
  };
}

export interface PaymentHistory {
  success: boolean;
  message: string;
  data: FinanceTransaction[];
  pagination?: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface PaymentStats {
  success: boolean;
  message: string;
  data: {
    totalDeposits: number;
    totalWithdrawals: number;
    totalCompleted: number;
    totalPending: number;
    totalFailed: number;
    totalAmount: number;
    currency: string;
  };
}

export interface PaymentOptions {
  success: boolean;
  message: string;
  data: {
    mpesa: {
      enabled: boolean;
      minAmount: number;
      maxAmount: number;
      currency: string;
      description: string;
      supportedPhoneFormats: string[];
      processingTime: string;
      fees: string;
    };
    userTypes: {
      competitor: {
        description: string;
        minAmount: number;
        maxAmount: number;
      };
      student: {
        description: string;
        minAmount: number;
        maxAmount: number;
      };
      teacher: {
        description: string;
        minAmount: number;
        maxAmount: number;
      };
    };
  };
}

export interface BalanceInfo {
  success: boolean;
  message: string;
  data: {
    amount: number;
    currency: string;
    lastUpdated: Date;
  };
}

class FinanceService {
  private static instance: FinanceService;

  private constructor() {}

  public static getInstance(): FinanceService {
    if (!FinanceService.instance) {
      FinanceService.instance = new FinanceService();
    }
    return FinanceService.instance;
  }

  /**
   * Initiate MPESA deposit
   */
  async initiateDeposit(paymentRequest: MPESAPaymentRequest): Promise<MPESAPaymentResponse> {
    try {
      const response = await api.post('/payments/deposit', paymentRequest);
      return response.data;
    } catch (error: any) {
      console.error('Deposit initiation failed:', error);
      throw new Error(error.response?.data?.message || 'Failed to initiate deposit');
    }
  }

  /**
   * Initiate MPESA withdrawal (using deposit endpoint with negative amount)
   */
  async initiateWithdrawal(paymentRequest: MPESAPaymentRequest): Promise<MPESAPaymentResponse> {
    try {
      // For withdrawals, we'll use a special endpoint or modify the request
      const withdrawalRequest = {
        ...paymentRequest,
        type: 'withdrawal',
        amount: Math.abs(paymentRequest.amount) // Ensure positive amount
      };
      
      const response = await api.post('/payments/withdrawal', withdrawalRequest);
      return response.data;
    } catch (error: any) {
      console.error('Withdrawal initiation failed:', error);
      throw new Error(error.response?.data?.message || 'Failed to initiate withdrawal');
    }
  }

  /**
   * Check payment status
   */
  async checkPaymentStatus(checkoutRequestId: string): Promise<PaymentStatus> {
    try {
      const response = await api.get(`/payments/status/${checkoutRequestId}`);
      return response.data;
    } catch (error: any) {
      console.error('Payment status check failed:', error);
      throw new Error(error.response?.data?.message || 'Failed to check payment status');
    }
  }

  /**
   * Get user payment history
   */
  async getPaymentHistory(limit: number = 10, offset: number = 0): Promise<PaymentHistory> {
    try {
      const response = await api.get(`/payments/history?limit=${limit}&offset=${offset}`);
      return response.data;
    } catch (error: any) {
      console.error('Payment history fetch failed:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch payment history');
    }
  }

  /**
   * Get user payment statistics
   */
  async getPaymentStats(): Promise<PaymentStats> {
    try {
      const response = await api.get('/payments/stats');
      return response.data;
    } catch (error: any) {
      console.error('Payment stats fetch failed:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch payment statistics');
    }
  }

  /**
   * Get payment options and limits
   */
  async getPaymentOptions(): Promise<PaymentOptions> {
    try {
      const response = await api.get('/payments/options');
      return response.data;
    } catch (error: any) {
      console.error('Payment options fetch failed:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch payment options');
    }
  }

  /**
   * Validate phone number format
   */
  async validatePhoneNumber(phoneNumber: string): Promise<{ isValid: boolean; formatted?: string }> {
    try {
      const response = await api.post('/payments/validate-phone', { phoneNumber });
      return response.data.data;
    } catch (error: any) {
      console.error('Phone validation failed:', error);
      return { isValid: false };
    }
  }

  /**
   * Get user balance
   */
  async getUserBalance(): Promise<BalanceInfo> {
    try {
      console.log('Fetching user balance from /deposits/balance'); // Debug log
      const response = await api.get('/deposits/balance');
      console.log('Balance API response:', response); // Debug log
      return response.data;
    } catch (error: any) {
      console.error('Balance fetch failed:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      throw new Error(error.response?.data?.message || 'Failed to fetch user balance');
    }
  }

  /**
   * Get user deposits
   */
  async getUserDeposits(limit: number = 10, offset: number = 0): Promise<PaymentHistory> {
    try {
      const response = await api.get(`/deposits?limit=${limit}&offset=${offset}`);
      return response.data;
    } catch (error: any) {
      console.error('Deposits fetch failed:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch deposits');
    }
  }

  /**
   * Get deposit statistics
   */
  async getDepositStats(): Promise<PaymentStats> {
    try {
      const response = await api.get('/deposits/stats');
      return response.data;
    } catch (error: any) {
      console.error('Deposit stats fetch failed:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch deposit statistics');
    }
  }

  /**
   * Cancel a pending deposit
   */
  async cancelDeposit(depositId: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.delete(`/deposits/${depositId}`);
      return response.data;
    } catch (error: any) {
      console.error('Deposit cancellation failed:', error);
      throw new Error(error.response?.data?.message || 'Failed to cancel deposit');
    }
  }

  /**
   * Poll payment status until completion or failure
   */
  async pollPaymentStatus(
    checkoutRequestId: string, 
    maxAttempts: number = 30, 
    intervalMs: number = 2000
  ): Promise<PaymentStatus> {
    let attempts = 0;
    
    while (attempts < maxAttempts) {
      try {
        const status = await this.checkPaymentStatus(checkoutRequestId);
        
        // If payment is completed or failed, return the status
        if (status.data.status === 'completed' || status.data.status === 'failed') {
          return status;
        }
        
        // Wait before next attempt
        await new Promise(resolve => setTimeout(resolve, intervalMs));
        attempts++;
        
      } catch (error) {
        console.error(`Payment status check attempt ${attempts + 1} failed:`, error);
        attempts++;
        
        // If we've exhausted all attempts, throw the error
        if (attempts >= maxAttempts) {
          throw error;
        }
        
        // Wait before retry
        await new Promise(resolve => setTimeout(resolve, intervalMs));
      }
    }
    
    throw new Error('Payment status polling timed out');
  }

  /**
   * Process a complete financial transaction
   */
  async processTransaction(
    type: 'deposit' | 'withdrawal',
    amount: number,
    phoneNumber: string,
    description?: string
  ): Promise<FinanceTransaction> {
    try {
      const userType = 'competitor'; // Hardcoded for competitors dashboard
      
      if (type === 'deposit') {
        const response = await this.initiateDeposit({
          amount,
          phoneNumber,
          userType,
          description: description || `Deposit via MPESA`
        });
        
        // Poll for completion
        const status = await this.pollPaymentStatus(response.data.checkoutRequestID);
        
        // Create transaction record
        const transaction: FinanceTransaction = {
          id: response.data.checkoutRequestID,
          type: 'deposit',
          amount,
          status: status.data.status as 'pending' | 'completed' | 'failed',
          mpesaReference: status.data.mpesaReceiptNumber,
          timestamp: new Date(),
          description: description || `Deposit via MPESA`,
          phoneNumber,
          userType,
          metadata: {
            checkoutRequestId: response.data.checkoutRequestID,
            merchantRequestId: response.data.merchantRequestID,
            resultCode: status.data.resultCode,
            resultDesc: status.data.resultDesc
          }
        };
        
        return transaction;
        
      } else {
        // Handle withdrawal
        const response = await this.initiateWithdrawal({
          amount,
          phoneNumber,
          userType,
          description: description || `Withdrawal via MPESA`
        });
        
        // Poll for completion
        const status = await this.pollPaymentStatus(response.data.checkoutRequestID);
        
        // Create transaction record
        const transaction: FinanceTransaction = {
          id: response.data.checkoutRequestID,
          type: 'withdrawal',
          amount,
          status: status.data.status as 'pending' | 'completed' | 'failed',
          mpesaReference: status.data.mpesaReceiptNumber,
          timestamp: new Date(),
          description: description || `Withdrawal via MPESA`,
          fee: Math.ceil(amount * 0.01), // 1% fee
          netAmount: amount - Math.ceil(amount * 0.01),
          phoneNumber,
          userType,
          metadata: {
            checkoutRequestId: response.data.checkoutRequestID,
            merchantRequestId: response.data.merchantRequestID,
            resultCode: status.data.resultCode,
            resultDesc: status.data.resultDesc
          }
        };
        
        return transaction;
      }
      
    } catch (error: any) {
      console.error('Transaction processing failed:', error);
      throw new Error(error.message || 'Failed to process transaction');
    }
  }
}

export default FinanceService.getInstance();
